import React, { useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Loader2, Check } from "lucide-react";
import api from "@/lib/api";

/** Parses a bullet key of the shape "experience_<entryIdx>_<bulletIdx>" (also projects/
 * internships) and replaces just that one bullet's text -- keeps every other field untouched. */
function applyQuantifiedBullet(draft, key, quantifiedText) {
  const match = key.match(/^(experience|projects|internships)_(\d+)_(\d+)$/);
  if (!match) return draft;
  const [, section, entryIdxStr, bulletIdxStr] = match;
  const entryIdx = parseInt(entryIdxStr, 10);
  const bulletIdx = parseInt(bulletIdxStr, 10);
  const list = draft[section].map((entry, i) => {
    if (i !== entryIdx) return entry;
    const bullets = entry.bullets.map((b, j) => (j === bulletIdx ? quantifiedText : b));
    return { ...entry, bullets };
  });
  return { ...draft, [section]: list };
}

/** AI Tools -> "Achievement Quantifier": finds experience/project/internship bullets missing a
 * number/metric and suggests a quantified rewrite for each, applied one-by-one on demand. */
export default function QuantifyAchievementsCard({ draft, setDraft }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [applied, setApplied] = useState(new Set());

  const findSuggestions = async () => {
    setLoading(true);
    setSuggestions(null);
    setApplied(new Set());
    try {
      const { data } = await api.post("/resume-builder/quantify-achievements", { draft });
      if (data.suggestions?.length) setSuggestions(data.suggestions);
      else toast.success(data.message || "Every bullet already includes a number or metric!");
    } catch {
      toast.error("Could not check your achievements right now");
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (s) => {
    setDraft(applyQuantifiedBullet(draft, s.key, s.quantified));
    setApplied((prev) => new Set(prev).add(s.key));
    toast.success("Bullet updated with a quantified metric");
  };

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="ai-tool-card-quantify">
      <div className="flex items-center gap-2 mb-1.5">
        <TrendingUp className="w-4 h-4 text-indigo-400" />
        <p className="text-sm font-semibold text-white">Achievement Quantifier</p>
      </div>
      <p className="text-xs text-slate-400 mb-3">Find bullets missing a number/metric and turn them into quantified, impact-driven achievements.</p>
      <button
        onClick={findSuggestions} disabled={loading} data-testid="ai-tool-quantify-button"
        className="flex items-center justify-center gap-1.5 w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />} Find Weak Bullets
      </button>

      {suggestions?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="ai-tool-quantify-results">
          {suggestions.map((s) => (
            <div key={s.key} className="bg-white/5 rounded-lg p-2.5" data-testid={`ai-tool-quantify-suggestion-${s.key}`}>
              <p className="text-xs text-slate-400 line-through">{s.original}</p>
              <p className="text-xs text-emerald-300 mt-0.5">{s.quantified}</p>
              <button
                onClick={() => applySuggestion(s)} disabled={applied.has(s.key)} data-testid={`ai-tool-quantify-apply-${s.key}`}
                className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-70"
              >
                {applied.has(s.key) ? <span className="text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Applied</span> : "Apply this rewrite"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
